import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';

function PaymentTermDrawer({ paymentTerm, onClose, onSaved, onDeleted }) {
  const title = `${paymentTerm?.id ? 'Edit' : 'New'} Payment Terms`;
  useDocumentTitle(title);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const [hasDependencies, setHasDependencies] = useState(true);

  useEffect(() => {
    if (!paymentTerm?.id) {
      setHasDependencies(false);
      return;
    }

    (async function fetchData() {
      const dependencies = await api.www.workspaces(workspace.id).paymentTerms(paymentTerm.id).hasDependencies();
      setHasDependencies(dependencies.data);
    })();
  }, [api, paymentTerm, workspace]);

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  const initialValues = mergeValues({ type: 'net', days: 1, name: 'Net-1' }, paymentTerm);

  return (
    <Drawer
      isOpen
      title={title}
      byline="Custom Data"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values, { resetForm }) {
          try {
            form.submit(formRef.current.status.action);

            const parseValues = (body) => {
              let toSave = { ...body };
              switch (body.type) {
                case 'net':
                  toSave.name = values.name || `Net-${toSave.days}`;
                  break;
                case 'net_end_of_month':
                  toSave.name = values.name || `Net-${toSave.days} End-of-Month`;
                  break;
              }
              return toSave;
            };

            const body = emptyStringToNull({ ...parseValues(values) });

            const { data } = await api.www.workspaces(workspace.id).paymentTerms(paymentTerm?.id).upsert(body);

            await onSaved(data);
            form.save(formRef.current.status.action);

            switch (formRef.current.status.action) {
              case 'new':
                resetForm();
                toast.success('Payment Terms successfully saved.');
                break;
              case 'close':
                closeDrawer();
                break;
            }
            form.done();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        async function handleDelete() {
          const confirm = await confirmation.prompt((resolve) => (
            <DeleteConfirmation resolve={resolve}>
              Are you sure that you want to delete these Payment Terms?
            </DeleteConfirmation>
          ));
          if (!confirm) return;
          try {
            await api.www.workspaces(workspace.id).paymentTerms(paymentTerm.id).delete();
            await onDeleted();
            closeDrawer();
          } catch ({ message }) {
            toast.error(message);
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              type: Yup.string().label('Type').max(255).required(),
              days: Yup.number().label('Days').min(0).max(365).nullable().required(),
            })}>
            {(formik) => {
              const submit = async (action) => {
                formik.setStatus({ action });
                formik.submitForm();
              };

              function generateName(type, days) {
                if (!type || !days) return '';
                switch (type) {
                  case 'net':
                    return `Net-${days}`;
                  case 'net_end_of_month':
                    return `Net-${days} End of Month`;
                  default:
                    return '';
                }
              }

              function handleTypeChange(value, formik) {
                formik.setFieldValue('type', value);

                if (!paymentTerm?.id) {
                  const days = formik.values.days;
                  const newName = generateName(value, days);
                  formik.setFieldValue('name', newName);
                }
              }

              function handleDaysChange(value, formik) {
                formik.setFieldValue('days', value);

                if (!paymentTerm?.id) {
                  const type = formik.values.type;
                  const newName = generateName(type, value);
                  formik.setFieldValue('name', newName);
                }
              }

              return (
                <Form>
                  {status && <FormMessage.Error>{message}</FormMessage.Error>}
                  <Form.Section
                    title="Details"
                    subtitle="Net terms are due the specified number of days after the invoice issue date. Net End-of-Month terms are due the specified number of days after the end of the month in which the invoice is issued.">
                    <Form.Control>
                      <Field.SingleSelect
                        onChange={({ target: { value } }) => handleTypeChange(value, formik)}
                        autoFocus
                        name="type"
                        placeholder="Type">
                        <option key="net" value="net">
                          Net
                        </option>
                        <option key="net_end_of_month" value="net_end_of_month">
                          Net End-of-Month
                        </option>
                      </Field.SingleSelect>
                    </Form.Control>
                    <Form.Control>
                      <Field.Number
                        onChange={(value) => handleDaysChange(value, formik)}
                        value={formik.values.days}
                        name="days"
                        placeholder="Days"
                        min={1}
                        max={365}
                        style={{ textAlign: 'left' }}
                      />
                    </Form.Control>
                    <Form.Control>
                      <Field.Text
                        value={formik.values.name}
                        name="name"
                        placeholder="Name"
                        style={{ textAlign: 'left' }}
                      />
                    </Form.Control>
                  </Form.Section>

                  <Drawer.Actions>
                    {paymentTerm.id && !hasDependencies && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                      {!paymentTerm?.id && (
                        <ActionButton
                          isLoading={isSubmitting === 'new'}
                          ok={saved === 'new'}
                          type="submit"
                          onClick={() => submit('new')}>
                          Save &amp; New
                        </ActionButton>
                      )}
                      <ActionButton
                        isLoading={isSubmitting === 'close'}
                        ok={saved === 'close'}
                        type="submit"
                        onClick={() => submit('close')}>
                        Save &amp; Close
                      </ActionButton>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

export default PaymentTermDrawer;
